import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Icon, Button, getAccessToken } from '@iq/react-components';
import printJS from 'print-js';

import Tabs, { Tab } from '../Tabs/Tabs';
import BigModal from '../BigModal';
import PdfPreview from './types/PdfPreview';
import VideoPreview from './types/VideoPreview';
import ImagePreview from './types/ImagePreview';
import TextPreview from './types/TextPreview';
import BookmarkPanel from './BookmarkPanel';
import FileBookmarks from './FileBookmarks';
import FiletypeIcon from '../FiletypeIcon';

import Loader from '../Loader';
import FileLink from '../FileLink';
import { useClientSizeRefresh } from '../../utils';
import { checkIsOnline, displayNotification, getNotification } from '../../bundles/notifications';
import {
  MALWARE_SCAN_STATE,
  MALWARE_SCAN_STATUS_CLEAN,
  downloadFiles as downloadFileAction,
  useMalwareScanState,
} from '../../bundles/files';
import { downloadFileBlob } from '../../services';
import config from '../../config';

const MALWARE_SCAN_MESSAGES = {
  [MALWARE_SCAN_STATE.SCANNING]:
    'This file is still being scanned for malware. Preview and download are available once the scan completes.',
  [MALWARE_SCAN_STATE.MALICIOUS]: 'This file was blocked because malware was detected.',
  [MALWARE_SCAN_STATE.UNVERIFIED]:
    'This file is blocked because the malware scan could not be verified.',
};

const supportedFiles = new Set([
  // Text
  'json',
  'md',
  'txt',

  // Pdf
  'pdf',

  // Images
  'apng',
  'bmp',
  'gif',
  'ico',
  'jpeg',
  'jpg',
  'jfif',
  'pjpeg',
  'pjp',
  'png',
  'svg',
  'tif',
  'tiff',
  'webp',

  // Video
  '3gp',
  'avi',
  'mov',
  'mp4',
  'ogv',
  'webm',
]);

const RelatedFiles = ({ files, onPreview }) => (
  <div className="related-files-component custom-thin-scrollbar">
    {files.map((item) => (
      <FileLink
        file={item}
        key={item.id}
        onClick={() => onPreview(item)}
      />
    ))}
  </div>
);

const openFileInNewTab = async (id) => {
  try {
    const token = await getAccessToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const fileEndpoint = config.services.file.endpoint.replace(/\/$/, '');
    const url = `${fileEndpoint}/files/${id}/blob?disp=inline`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${id}`);
    }
    const blob = await response.blob();
    const blobUrl = globalThis.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');

    // Cleanup after 10 seconds
    setTimeout(() => globalThis.URL.revokeObjectURL(blobUrl), 10_000);
  } catch (error) {
    console.error('Error opening file in new tab:', error);
  }
};

const ChangeFile = ({ onStepPrevious, onStepNext, canToggleNext, file }) => (
  <div className="change-file">
    {onStepPrevious && (
      <Button
        activity="secondary"
        disabled={!canToggleNext}
        onClick={() => onStepPrevious(file.id)}
        icon={
          <Icon
            size="xs"
            icon="he-backwards"
          />
        }
        tooltip="Previous"
        tooltipPosition="bottom"
      />
    )}
    {onStepNext && (
      <Button
        activity="secondary"
        disabled={!canToggleNext}
        onClick={() => onStepNext(file.id)}
        icon={
          <Icon
            size="xs"
            icon="he-forwards"
          />
        }
        tooltip="Next"
        tooltipPosition="bottom"
      />
    )}
  </div>
);

const Toolbar = ({
  handleClickPrint,
  handleClickDownload,
  handleClickCreateBookmark,
  handleToggleFullscreen,
  downloadedFile,
  fullScreenRef,
  file,
  enableBookmarks,
  contractedSidebar,
  openFileInNewTab,
  isDownloadDisabled,
}) => (
  <div className={contractedSidebar ? 'bottom-toolbar-contracted' : 'bottom-toolbar'}>
    {(file.ext === 'pdf' || file.fileType === 'image') && (
      <Button
        activity="secondary"
        onClick={() => handleClickPrint(file.ext === 'pdf' ? 'pdf' : 'image')}
        disabled={!downloadedFile}
        icon={<Icon icon="he-print" />}
        tooltip="Print"
        tooltipPosition={contractedSidebar ? 'top-end' : 'top'}
      />
    )}
    <Button
      data-testid="download-button"
      activity="secondary"
      disabled={isDownloadDisabled}
      onClick={handleClickDownload}
      icon={<Icon icon="he-download" />}
      tooltip="Download"
      tooltipPosition={contractedSidebar ? 'top-end' : 'top'}
    />
    {downloadedFile && fullScreenRef.current && (
      <>
        {enableBookmarks && (
          <Button
            activity="secondary"
            onClick={handleClickCreateBookmark}
            icon={<Icon icon="he-tag" />}
            tooltip="Create bookmark"
            tooltipPosition={contractedSidebar ? 'top-end' : 'top'}
          />
        )}
        <Button
          activity="secondary"
          onClick={handleToggleFullscreen}
          icon={
            <Icon
              size="s"
              icon="he-fullscreen"
            />
          }
          tooltip="Full screen"
          tooltipPosition={contractedSidebar ? 'top-end' : 'top'}
        />
        {(file.ext === 'pdf' || file.fileType === 'image') && (
          <Button
            activity="secondary"
            icon={<Icon icon="he-popup" />}
            tooltip="Open in new window"
            tooltipPosition={contractedSidebar ? 'top-end' : 'top'}
            onClick={() => openFileInNewTab(file.id)}
          />
        )}
      </>
    )}
  </div>
);

const PreviewComponent = ({
  shouldDownloadFile,
  downloadedFile,
  downloadedFileType,
  malwareScanState,
  file,
  fullScreenRef,
  setCurrentBookmarkState,
  activeBookmark,
  setActiveBookmark,
  setFileInfo,
  zoomLevel,
  displayBookmarkPanel,
  contractedSidebar,
  setIsPdf,
  onStepPrevious,
  onStepNext,
}) => {
  useEffect(() => {
    setIsPdf(downloadedFileType === 'pdf');
  }, [downloadedFileType, setIsPdf]);

  const handleOnStepPrevious = useCallback(() => {
    if (onStepPrevious) {
      onStepPrevious(file.id);
    }
  }, [onStepPrevious, file.id]);

  const handleOnStepNext = useCallback(() => {
    if (onStepNext) {
      onStepNext(file.id);
    }
  }, [onStepNext, file.id]);

  if (shouldDownloadFile && (!downloadedFile || !downloadedFileType)) {
    return (
      <div className="file-preview-loader">
        <Loader text="Loading file..." />
      </div>
    );
  }

  const bookmark =
    activeBookmark === null ? null : file.bookmarks.find(({ id }) => id === activeBookmark);

  if (downloadedFile instanceof Error) {
    return <div style={{ paddingLeft: '1.5rem' }}>{downloadedFile.message}</div>;
  }

  if (downloadedFileType === 'pdf') {
    return (
      <PdfPreview
        activeBookmark={bookmark}
        setActiveBookmark={setActiveBookmark}
        file={downloadedFile}
        fullScreenRef={fullScreenRef}
        setBookmarkState={setCurrentBookmarkState}
        setFileInfo={setFileInfo}
        contractedSidebar={contractedSidebar}
        onStepPrevious={handleOnStepPrevious}
        onStepNext={handleOnStepNext}
      />
    );
  }

  if (downloadedFileType === 'video') {
    return (
      <VideoPreview
        file={file}
        videoRef={fullScreenRef}
        setBookmarkState={setCurrentBookmarkState}
        activeBookmark={bookmark}
        setActiveBookmark={setActiveBookmark}
        setFileInfo={setFileInfo}
        displayBookmarkPanel={displayBookmarkPanel}
      />
    );
  }

  if (downloadedFileType === 'image') {
    return (
      <ImagePreview
        file={downloadedFile}
        fullScreenRef={fullScreenRef}
        zoomLevel={zoomLevel}
        setFileInfo={setFileInfo}
      />
    );
  }

  if (downloadedFileType === 'text') {
    return (
      <TextPreview
        file={downloadedFile}
        fullScreenRef={fullScreenRef}
        setFileInfo={setFileInfo}
      />
    );
  }

  const malwareScanMessage = MALWARE_SCAN_MESSAGES[malwareScanState];

  return (
    <div className="file-preview-icon-container">
      {malwareScanMessage ? (
        <p data-testid="malware-scan-message">{malwareScanMessage}</p>
      ) : (
        <div className="file-preview-icon">
          <FiletypeIcon file={file} />
        </div>
      )}
    </div>
  );
};

const FilePreviewModal = ({
  fileId,
  files = [],
  activeBookmark,
  setActiveBookmark,
  onCloseModal,
  onStepNext,
  onStepPrevious,
  onPreview,
  canEdit,
}) => {
  const fullScreenReference = useRef(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [canToggleNext, setCanToggleNext] = useState(false);
  const [shouldDownloadFile, setShouldDownloadFile] = useState(false);
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [downloadedFileType, setDownloadedFileType] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [displayBookmarkPanel, setDisplayBookmarkPanel] = useState(false);
  const [currentBookmarkState, setCurrentBookmarkState] = useState();
  const [contractedSidebar, setContractedSidebar] = useState(true);
  const [isPdf, setIsPdf] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null);

  const refreshClientSize = useClientSizeRefresh();
  const dispatch = useDispatch();

  const file = files.find((f) => f.id === fileId);
  const malwareScanState = useMalwareScanState(file);
  const isFileClean = malwareScanState === MALWARE_SCAN_STATE.CLEAN;

  let fileType;
  if (file.mime && file.mime.includes('pdf')) {
    fileType = 'pdf';
  } else {
    fileType = file.mime ? file.mime.split('/').shift() : null;
  }

  const enableBookmarks = ['video', 'pdf'].includes(fileType);
  const enableEditBookmarks = enableBookmarks && canEdit;
  const relatedFiles = files.filter(({ id }) => id !== file.id);
  const isSupportedFile = supportedFiles.has(file.filename?.toLowerCase().split('.').pop());

  useEffect(() => {
    function handleKeyDown(event) {
      const pressedKey = event.key || event.code;
      const pressedKeyCode = event.keyCode || event.which;
      const isRightArrow = pressedKey === 'ArrowRight' || pressedKeyCode === 39;
      const isLeftArrow = pressedKey === 'ArrowLeft' || pressedKeyCode === 37;
      const isDownArrow = pressedKey === 'ArrowDown' || pressedKeyCode === 40;
      const isUpArrow = pressedKey === 'ArrowUp' || pressedKeyCode === 38;

      if (displayBookmarkPanel) return;
      if (canToggleNext && isRightArrow && onStepNext) {
        event.preventDefault();
        event.stopPropagation();
        onStepNext(file.id);
      }
      if (canToggleNext && isLeftArrow && onStepPrevious) {
        event.preventDefault();
        event.stopPropagation();
        onStepPrevious(file.id);
      }
      if (isDownArrow && fullScreenReference.current) {
        fullScreenReference.current.scrollBy(0, 40);
      }
      if (isUpArrow && fullScreenReference.current) {
        fullScreenReference.current.scrollBy(0, -40);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canToggleNext, displayBookmarkPanel, onStepNext, onStepPrevious, file.id]);

  const downloadFile = useCallback(async () => {
    try {
      const blob = await downloadFileBlob(file.id);
      if (fileType.includes('video')) {
        const objectUrl = URL.createObjectURL(blob);
        file.sourceUrl = objectUrl;
        setDownloadedFile(file);
      } else {
        const fileObject = new File([blob], file.filename);
        setDownloadedFile(fileObject);
      }
    } catch (error) {
      setDownloadedFile(error);
      console.error('Unable to download file:', error);
      dispatch(checkIsOnline());
      dispatch(displayNotification(getNotification('filePreviewDownload', 'error')()));
    }
    setDownloadedFileType(fileType);

    if (fileType !== 'pdf') setCanToggleNext(true);
  }, [file, fileType, dispatch]);

  useEffect(() => {
    setDownloadedFile(null);
    setDownloadedFileType(null);
    setZoomLevel(0);
    setFileInfo(null);

    if (isSupportedFile && isFileClean) {
      setCanToggleNext(false);
      setShouldDownloadFile(true);
      downloadFile();
    } else {
      setCanToggleNext(true);
      setShouldDownloadFile(false);
      setDownloadedFile(null);
      setDownloadedFileType(null);
    }
  }, [file.id, isSupportedFile, isFileClean, downloadFile]);

  const handleClickDownload = () => {
    // The download button is only enabled for a clean file. The status is passed along because the
    // file is not always in the files store (comment attachments) and its stored status can be stale.
    dispatch(
      downloadFileAction({
        id: file.id,
        filename: file.filename || 'download',
        malwareScanStatus: MALWARE_SCAN_STATUS_CLEAN,
      })
    );
  };

  const handleClickPrint = (typeOfFile) => {
    printJS({
      printable: URL.createObjectURL(downloadedFile),
      type: typeOfFile,
    });
  };

  const handleToggleFullscreen = () => {
    const ref = fullScreenReference.current;
    if (ref.requestFullscreen) {
      ref.requestFullscreen();
    }
    if (ref.webkitRequestFullscreen) {
      ref.webkitRequestFullscreen();
    }
  };

  const handleClickCreateBookmark = () => {
    setDisplayBookmarkPanel(!displayBookmarkPanel);
  };

  const handleEditBookmark = (bookmark) => {
    setCurrentBookmarkState(bookmark);
    setDisplayBookmarkPanel(true);
  };

  const handleToggleSidebar = (tab) => {
    setSelectedTab(tab);
    setContractedSidebar(!contractedSidebar);
    refreshClientSize();
  };

  const onCloseBookmarkPanel = () => {
    setCurrentBookmarkState((previousState) => ({
      bookmarkData: previousState.bookmarkData,
    }));
    setDisplayBookmarkPanel(false);
  };

  return (
    <BigModal
      onClose={onCloseModal}
      className="file-preview-modal-component"
      overlayClassName="file-preview-modal-overlay"
    >
      <div className="close-preview-modal">
        <Icon
          size="xs"
          icon="he-close"
          style={{ color: '#f2f2f2' }}
          onClick={onCloseModal}
        />
      </div>
      <div className="file-preview-modal">
        <div className="preview-area">
          <div className={isPdf ? 'preview-content' : 'preview-content non-pdf-padding'}>
            {!isPdf && (
              <div className="top-toolbar">
                <div className="title">{file.filename}</div>
                <ChangeFile
                  onStepNext={onStepNext}
                  onStepPrevious={onStepPrevious}
                  canToggleNext={canToggleNext}
                  file={file}
                />
              </div>
            )}
            <PreviewComponent
              shouldDownloadFile={shouldDownloadFile}
              downloadedFile={downloadedFile}
              downloadedFileType={downloadedFileType}
              malwareScanState={malwareScanState}
              file={file}
              fullScreenRef={fullScreenReference}
              setCurrentBookmarkState={setCurrentBookmarkState}
              zoomLevel={zoomLevel}
              activeBookmark={activeBookmark}
              setActiveBookmark={setActiveBookmark}
              setFileInfo={setFileInfo}
              displayBookmarkPanel={displayBookmarkPanel}
              contractedSidebar={contractedSidebar}
              setIsPdf={setIsPdf}
              onStepPrevious={onStepPrevious}
              onStepNext={onStepNext}
            />
          </div>
          <div className={contractedSidebar ? 'preview-sidebar contracted' : 'preview-sidebar'}>
            <div
              className={isPdf ? 'sidebar-icons pdf-top-margin' : 'sidebar-icons'}
              style={{ display: contractedSidebar ? 'initial' : 'none' }}
            >
              {enableBookmarks && (
                <Button
                  activity="secondary"
                  onClick={() => handleToggleSidebar('bookmarks')}
                  icon={<Icon icon="bookmark" />}
                  tooltip="View bookmarks"
                  tooltipPosition="bottom-end"
                />
              )}
              <Button
                activity="secondary"
                onClick={() => handleToggleSidebar('related')}
                icon={<Icon icon="he-list" />}
                tooltip="View related items"
                tooltipPosition="bottom-end"
              />
            </div>
            <div
              style={{
                display: contractedSidebar ? 'none' : 'initial',
                height: 'calc(100% - 56px)',
              }}
            >
              <Tabs
                selectedTab={selectedTab}
                actions={
                  <Button
                    activity="secondary"
                    onClick={handleToggleSidebar}
                    style={{ background: 'none' }}
                    icon={<Icon icon="he-forwards" />}
                    tooltip="Close sidebar"
                    tooltipPosition="bottom-end"
                  />
                }
              >
                {enableBookmarks && (
                  <Tab
                    id="bookmarks"
                    title={
                      <Button
                        activity="secondary"
                        className="tab-button"
                        icon={<Icon icon="bookmark" />}
                        tooltip="Bookmarks"
                        tooltipPosition="bottom-start"
                      />
                    }
                  >
                    <FileBookmarks
                      file={file}
                      activeBookmark={activeBookmark}
                      setActiveBookmark={setActiveBookmark}
                      onEditBookmark={handleEditBookmark}
                      canEditBookmarks={enableEditBookmarks}
                    />
                  </Tab>
                )}
                <Tab
                  id="related"
                  title={
                    <Button
                      activity="secondary"
                      className="tab-button"
                      icon={<Icon icon="he-list" />}
                      tooltip="Related items"
                      tooltipPosition="bottom-start"
                    />
                  }
                >
                  <RelatedFiles
                    files={relatedFiles}
                    onPreview={onPreview}
                  />
                </Tab>
              </Tabs>
            </div>
            <Toolbar
              onStepNext={onStepNext}
              onStepPrevious={onStepPrevious}
              canToggleNext={canToggleNext}
              handleClickPrint={handleClickPrint}
              handleClickDownload={handleClickDownload}
              downloadedFile={downloadedFile}
              fullScreenRef={fullScreenReference}
              handleClickCreateBookmark={handleClickCreateBookmark}
              handleToggleFullscreen={handleToggleFullscreen}
              file={file}
              enableBookmarks={enableEditBookmarks}
              fileInfo={fileInfo}
              contractedSidebar={contractedSidebar}
              openFileInNewTab={openFileInNewTab}
              isDownloadDisabled={!isFileClean}
            />
          </div>
        </div>

        {/* {!!file.bookmarkData && (
          <BookmarkData bookmark={file} fileType={downloadedFileType} />
        )} */}

        {fileInfo !== null && displayBookmarkPanel && (
          <div className="create-bookmark-backdrop">
            <BookmarkPanel
              file={file}
              bookmark={currentBookmarkState}
              fileInfo={fileInfo}
              onClose={onCloseBookmarkPanel}
            />
          </div>
        )}
      </div>
    </BigModal>
  );
};

export default FilePreviewModal;
